import { Helmet } from 'react-helmet-async';

const SEO = ({ title, description }) => {
  const siteUrl = process.env.REACT_APP_BACKEND_URL?.replace('/api', '') || window.location.origin;
  
  // Default values for homepage
  const pageTitle = title || 'Highest Good Ventures | Kristen Barnett';
  const pageDescription = description || 'Kristen Barnett is the founder of Highest Good Ventures and creator of Tarot for Bros™ — a tarot card company reimagining traditional tarot decks for men. Based in Mill Valley, CA.';
  
  const structuredData = {
    "@context": "https://schema.org",
    "@type": "Person",
    "name": "Kristen Barnett",
    "url": siteUrl,
    "sameAs": [
      "https://www.linkedin.com/in/kcbarnett/",
      "https://www.instagram.com/itskristenbarnett/"
    ],
    "jobTitle": "Founder",
    "worksFor": {
      "@type": "Organization",
      "name": "Highest Good Ventures",
      "url": siteUrl
    },
    "description": "Kristen Barnett is the founder of Highest Good Ventures and creator of Tarot for Bros™, a tarot deck designed for men.",
    "alumniOf": "Cornell University",
    "address": {
      "@type": "PostalAddress",
      "addressLocality": "Mill Valley",
      "addressRegion": "CA"
    }
  };

  const organizationData = {
    "@context": "https://schema.org",
    "@graph": [
      {
        "@type": "Person",
        "@id": `${siteUrl}/#kristen-barnett`,
        "name": "Kristen Barnett",
        "jobTitle": "Founder",
        "description": "Kristen Barnett is the founder of Highest Good Ventures and creator of Tarot for Bros™, a tarot deck designed for men.",
        "url": siteUrl,
        "worksFor": {
          "@type": "Organization",
          "name": "Highest Good Ventures"
        },
        "founder": [
          {
            "@type": "Organization",
            "name": "Highest Good Ventures"
          },
          {
            "@type": "Brand",
            "name": "Tarot for Bros™",
            "url": "https://www.tarotforbros.com/"
          }
        ],
        "sameAs": [
          "https://www.instagram.com/itskristenbarnett/",
          "https://www.linkedin.com/in/kcbarnett/",
          "https://itskristen.substack.com/",
          "https://www.instagram.com/tarotforbros"
        ],
        "alumniOf": {
          "@type": "CollegeOrUniversity",
          "name": "Cornell University"
        },
        "address": {
          "@type": "PostalAddress",
          "addressLocality": "Mill Valley",
          "addressRegion": "CA"
        }
      },
      {
        "@type": "Organization",
        "@id": `${siteUrl}/#organization`,
        "name": "Highest Good Ventures",
        "alternateName": "HGV",
        "url": siteUrl,
        "logo": `${siteUrl}/logo.png`,
        "description": "Highest Good Ventures is an incubator at the intersection of the spiritual economy and regenerative capitalism, founded by Kristen Barnett.",
        "founder": {
          "@type": "Person",
          "name": "Kristen Barnett"
        },
        "sameAs": [
          "https://www.instagram.com/itskristenbarnett/",
          "https://www.linkedin.com/in/kcbarnett/"
        ],
        "subOrganization": [
          {
            "@type": "Brand",
            "name": "Tarot for Bros™",
            "url": "https://www.tarotforbros.com/",
            "description": "Tarot for Bros™ is a tarot card company that creates decks for men, translating ancient wisdom for the modern professional.",
            "sameAs": [
              "https://www.instagram.com/tarotforbros",
              "https://www.tiktok.com/@tarot.for.bros"
            ]
          }
        ]
      },
      {
        "@type": "WebSite",
        "@id": `${siteUrl}/#website`,
        "url": siteUrl,
        "name": "Kristen Barnett | Highest Good Ventures | Tarot for Bros™",
        "description": "Kristen Barnett is the founder of Highest Good Ventures and creator of Tarot for Bros™",
        "publisher": {
          "@id": `${siteUrl}/#organization`
        }
      },
      {
        "@type": "BreadcrumbList",
        "itemListElement": [
          {
            "@type": "ListItem",
            "position": 1,
            "name": "Kristen Barnett",
            "item": siteUrl
          },
          {
            "@type": "ListItem",
            "position": 2,
            "name": "Highest Good Ventures",
            "item": `${siteUrl}#about`
          },
          {
            "@type": "ListItem",
            "position": 3,
            "name": "Tarot for Bros™",
            "item": `${siteUrl}#tarot`
          }
        ]
      }
    ]
  };

  return (
    <Helmet>
      {/* Favicon */}
      <link rel="icon" type="image/png" href="https://customer-assets.emergentagent.com/job_good-ventures-1/artifacts/j6o0wqr7_Screenshot%202026-03-26%20at%2012.16.45%E2%80%AFPM.png" />
      <link rel="apple-touch-icon" href="https://customer-assets.emergentagent.com/job_good-ventures-1/artifacts/j6o0wqr7_Screenshot%202026-03-26%20at%2012.16.45%E2%80%AFPM.png" />
      
      {/* Primary Meta Tags */}
      <title>Highest Good Ventures | Kristen Barnett</title>
      <meta name="title" content="Highest Good Ventures | Kristen Barnett" />
      <meta name="description" content="Kristen Barnett is the founder of Highest Good Ventures and creator of Tarot for Bros™ — a tarot card company reimagining traditional tarot decks for men. Based in Mill Valley, CA." />
      <meta name="keywords" content="Kristen Barnett, Highest Good Ventures, Tarot for Bros, tarot company, tarot decks for men, spiritual economy, regenerative capitalism, entrepreneur, tarot card reader" />
      <meta name="author" content="Kristen Barnett" />
      
      {/* Canonical URL */}
      <link rel="canonical" href={siteUrl} />
      
      {/* Open Graph / Facebook */}
      <meta property="og:type" content="website" />
      <meta property="og:url" content={siteUrl} />
      <meta property="og:title" content={pageTitle} />
      <meta property="og:description" content={pageDescription} />
      <meta property="og:image" content={`${siteUrl}/og-image.jpg`} />
      <meta property="og:image:width" content="1200" />
      <meta property="og:image:height" content="630" />
      <meta property="og:site_name" content="Highest Good Ventures" />
      <meta property="og:locale" content="en_US" />

      {/* Twitter */}
      <meta property="twitter:card" content="summary_large_image" />
      <meta property="twitter:url" content={siteUrl} />
      <meta property="twitter:title" content={pageTitle} />
      <meta property="twitter:description" content={pageDescription} />
      <meta property="twitter:image" content={`${siteUrl}/og-image.jpg`} />

      {/* Additional Meta Tags */}
      <meta name="robots" content="index, follow" />
      <meta name="googlebot" content="index, follow" />
      <meta name="viewport" content="width=device-width, initial-scale=1.0" />
      <meta httpEquiv="Content-Type" content="text/html; charset=utf-8" />
      <meta name="language" content="English" />

      {/* Structured Data - Person Schema (Priority for Name Search) */}
      <script type="application/ld+json">
        {JSON.stringify(structuredData)}
      </script>
      
      {/* Structured Data - Organization Schema */}
      <script type="application/ld+json">
        {JSON.stringify(organizationData)}
      </script>
    </Helmet>
  );
};

export default SEO;
